import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from './ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import { usePlatformTimezone, formatInTimezone, zonedTimeToUtcIso, utcIsoToZonedInputValue } from '../utils/platformTimezone';
import {
  PencilSimple, Trash, Eye, EyeSlash, PaperPlaneRight, FloppyDisk,
  ArrowClockwise, CheckCircle, XCircle, Circle, WarningCircle, Plugs,
  ChartPie, Queue, Megaphone, Clock, ArrowsClockwise, Prohibit,
  CaretRight, DesktopTower, DeviceMobile, MagnifyingGlass, Plus,
  Funnel, ArrowSquareOut, Code, Copy,
} from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* ─── SMTP provider presets ────────────────────────────────────────────
 * Selecting a preset just fills host/port/encryption — username, sender
 * email and password are left for the admin to type so we don't leak
 * one user's credentials into another's account.
 */
const SMTP_PRESETS = [
  { key: 'gmail',    label: 'Gmail',            host: 'smtp.gmail.com',          port: 587, encryption: 'tls',
    hint: 'Requires a Gmail App Password (16 chars). Enable 2FA, then generate at myaccount.google.com/apppasswords.' },
  { key: 'outlook',  label: 'Outlook / M365',   host: 'smtp.office365.com',      port: 587, encryption: 'tls',
    hint: 'Username = full email. Modern auth tenants may need an App Password too.' },
  { key: 'yahoo',    label: 'Yahoo Mail',       host: 'smtp.mail.yahoo.com',     port: 587, encryption: 'tls',
    hint: 'Requires a Yahoo App Password. Generate it from Account Security → "App passwords".' },
  { key: 'custom',   label: 'Custom SMTP',      host: '',                         port: 587, encryption: 'tls',
    hint: 'Enter the host, port and encryption documented by your SMTP provider.' },
];

/* ─── Reusable secret input with show/hide toggle ─────────────────────
 * Starts EMPTY by default — typing replaces (never appends to) the
 * stored secret. When the server reports `*_set: true`, we render a
 * green "✓ configured" chip + leave-blank placeholder so the admin
 * knows their secret is preserved unless they explicitly type a new
 * value. Eye icon toggles visibility without clearing the field.
 */
const SecretInput = ({ value, onChange, configured, testId, placeholder }) => {
  const [visible, setVisible] = useState(false);
  return (
    <div className="relative">
      <Input
        type={visible ? 'text' : 'password'}
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder={configured ? '(leave blank to keep existing)' : (placeholder || '')}
        autoComplete="new-password"
        data-testid={testId}
        className="pr-20"
      />
      <button
        type="button"
        onClick={() => setVisible((v) => !v)}
        className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded text-text_muted hover:text-text_main"
        tabIndex={-1}
        title={visible ? 'Hide' : 'Show'}
        data-testid={`${testId}-toggle`}
      >
        {visible ? <EyeSlash size={16} /> : <Eye size={16} />}
      </button>
      {configured && !value && (
        <span
          className="absolute right-10 top-1/2 -translate-y-1/2 inline-flex items-center gap-1 text-[10px] font-semibold text-success"
          data-testid={`${testId}-configured`}
        >
          <CheckCircle size={12} weight="fill" /> configured
        </span>
      )}
    </div>
  );
};

const STAGE_ICON = {
  ok:      <CheckCircle size={16} weight="fill" className="text-success" />,
  fail:    <XCircle size={16} weight="fill" className="text-danger" />,
  pending: <Circle size={16} className="text-text_muted" />,
  skipped: <Circle size={16} weight="fill" className="text-text_muted opacity-50" />,
};
const STAGE_LABEL = {
  dns_resolution: 'DNS resolution',
  tcp_connect:    'TCP port reachability',
  tls_handshake:  'TLS / STARTTLS handshake',
  smtp_auth:      'SMTP authentication',
  ehlo:           'EHLO + capabilities',
};

/* ──────────────────────────────────────────────────────────────
   MAIN CONTAINER — 5 tabs
   ────────────────────────────────────────────────────────────── */
export const EmailNotificationsManagement = () => {
  return (
    <div data-testid="email-notifications-management">
      <div className="mb-4">
        <h2 className="text-xl font-bold">Email & Notifications</h2>
        <p className="text-sm text-text_muted">Dashboard, queue, campaigns, templates and provider settings.</p>
      </div>
      <Tabs defaultValue="dashboard" className="w-full">
        <TabsList className="mb-4">
          <TabsTrigger value="dashboard" data-testid="tab-dashboard">Dashboard</TabsTrigger>
          <TabsTrigger value="queue" data-testid="tab-queue">Queue</TabsTrigger>
          <TabsTrigger value="campaigns" data-testid="tab-campaigns">Campaigns</TabsTrigger>
          <TabsTrigger value="templates" data-testid="tab-templates">Templates</TabsTrigger>
          <TabsTrigger value="senders" data-testid="tab-senders">Senders</TabsTrigger>
          <TabsTrigger value="logs" data-testid="tab-logs">Logs</TabsTrigger>
          <TabsTrigger value="settings" data-testid="tab-settings">Settings</TabsTrigger>
        </TabsList>
        <TabsContent value="dashboard"><EmailDashboardPanel /></TabsContent>
        <TabsContent value="queue"><EmailQueuePanel /></TabsContent>
        <TabsContent value="campaigns"><EmailCampaignsPanel /></TabsContent>
        <TabsContent value="templates"><EmailTemplatesPanel /></TabsContent>
        <TabsContent value="senders"><EmailSendersPanel /></TabsContent>
        <TabsContent value="logs"><NotificationLogsPanel /></TabsContent>
        <TabsContent value="settings"><EmailSettingsPanel /></TabsContent>
      </Tabs>
    </div>
  );
};

/* ──────────────────────────────────────────────────────────────
   TAB 1 — Provider Settings
   ────────────────────────────────────────────────────────────── */
const EmailSettingsPanel = () => {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [testing, setTesting] = useState(false);
  const [diag, setDiag] = useState(null);          // { ok, summary, stages }
  const [diagRunning, setDiagRunning] = useState(false);
  const [presetKey, setPresetKey] = useState('');

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/email-settings`, { withCredentials: true });
      setS(data);
      // Reset preset selection on reload so the dropdown reflects the
      // freshly-loaded host instead of the last applied preset.
      setPresetKey('');
    } catch { toast.error('Failed to load settings'); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!s) return;
    setSaving(true);
    try {
      // Password / API key are sent ONLY when the admin typed a new
      // value. Empty string → backend treats as "keep existing".
      const payload = {
        mode: s.mode, dev_log_only: s.dev_log_only,
        sender_email: s.sender_email, sender_name: s.sender_name,
        smtp: {
          host: s.smtp.host, port: s.smtp.port, username: s.smtp.username,
          encryption: s.smtp.encryption,
          password: s.smtp.password || null,
        },
        external: {
          provider: s.external.provider,
          api_key: s.external.api_key || null,
        },
      };
      await axios.put(`${API}/admin/email-settings`, payload, { withCredentials: true });
      toast.success('Settings saved');
      // Reload so the freshly-saved values + `*_set` flags refresh, and
      // the password / api_key text inputs return to blank.
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  const sendTest = async () => {
    if (!testEmail) { toast.error('Enter a recipient email'); return; }
    setTesting(true);
    try {
      const { data } = await axios.post(`${API}/admin/email-settings/test`,
        { to_email: testEmail }, { withCredentials: true });
      // If dev_log_only is on, the API returns provider=DEV_LOG_ONLY +
      // delivered=false. Surface this clearly as a warning so admins
      // don't think a real email went out.
      if (data.delivered === false || data.provider === 'DEV_LOG_ONLY') {
        toast.warning(
          'Dev mode is ON — no real email was sent. Disable "Dev mode (log only)" to test real delivery.',
          { duration: 6000 }
        );
      } else {
        toast.success(`Email delivered to ${data.to} via ${data.provider}`);
      }
    } catch (e) { toast.error(e.response?.data?.detail || 'Test failed', { duration: 9000 }); }
    finally { setTesting(false); }
  };

  const runDiagnostics = async () => {
    setDiagRunning(true);
    setDiag(null);
    try {
      const { data } = await axios.post(
        `${API}/admin/email-settings/test-connection`, {},
        { withCredentials: true }
      );
      setDiag(data);
      if (data.ok) toast.success('All SMTP diagnostic stages passed');
      else toast.warning(`Diagnostics: ${data.summary}`, { duration: 6000 });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Diagnostics request failed');
    } finally { setDiagRunning(false); }
  };

  const applyPreset = (key) => {
    const p = SMTP_PRESETS.find((x) => x.key === key);
    if (!p) return;
    setPresetKey(key);
    setS({ ...s, smtp: { ...s.smtp, host: p.host, port: p.port, encryption: p.encryption } });
    if (p.hint) toast.message(p.label, { description: p.hint, duration: 7000 });
  };

  if (!s) return <p className="text-text_muted">Loading…</p>;

  const activePreset = SMTP_PRESETS.find((p) => p.key === presetKey)
    || SMTP_PRESETS.find((p) => p.host && p.host === s.smtp.host);

  return (
    <div className="space-y-5" data-testid="email-settings-panel">
      <div className="bg-white border border-border rounded-lg p-5 space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>Provider Mode</Label>
            <Select value={s.mode} onValueChange={(v) => setS({ ...s, mode: v })}>
              <SelectTrigger data-testid="mode-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="INTERNAL">INTERNAL (SMTP)</SelectItem>
                <SelectItem value="EXTERNAL">EXTERNAL (API)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-end gap-2">
            <input type="checkbox" id="dev-log-only" checked={s.dev_log_only}
              onChange={e => setS({ ...s, dev_log_only: e.target.checked })}
              data-testid="dev-log-toggle" />
            <Label htmlFor="dev-log-only" className="cursor-pointer">Dev mode (log only, don&apos;t send)</Label>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>Sender Email</Label>
            <Input value={s.sender_email} onChange={e => setS({ ...s, sender_email: e.target.value })}
              placeholder="noreply@ato.mg" data-testid="sender-email-input" />
          </div>
          <div>
            <Label>Sender Name</Label>
            <Input value={s.sender_name} onChange={e => setS({ ...s, sender_name: e.target.value })}
              placeholder="ATÔ" data-testid="sender-name-input" />
          </div>
        </div>
      </div>

      {s.mode === 'INTERNAL' && (
        <div className="bg-white border border-border rounded-lg p-5 space-y-4" data-testid="smtp-section">
          <div className="flex items-center justify-between gap-3">
            <h3 className="font-semibold">SMTP Configuration</h3>
            <div className="flex items-center gap-2">
              <Label className="text-xs text-text_muted">Preset:</Label>
              <Select value={presetKey || (activePreset?.key || '')} onValueChange={applyPreset}>
                <SelectTrigger className="w-44 h-9" data-testid="smtp-preset">
                  <SelectValue placeholder="Choose provider…" />
                </SelectTrigger>
                <SelectContent>
                  {SMTP_PRESETS.map((p) => (
                    <SelectItem key={p.key} value={p.key} data-testid={`smtp-preset-${p.key}`}>{p.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          {activePreset?.hint && (
            <div
              className="flex items-start gap-2 text-xs text-info bg-info-soft border border-info/20 rounded-md px-3 py-2"
              data-testid="smtp-preset-hint"
            >
              <WarningCircle size={14} weight="fill" className="flex-shrink-0 mt-0.5" />
              <span>{activePreset.hint}</span>
            </div>
          )}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Host</Label>
              <Input value={s.smtp.host} onChange={e => setS({ ...s, smtp: { ...s.smtp, host: e.target.value } })}
                placeholder="smtp.example.com" data-testid="smtp-host" />
            </div>
            <div>
              <Label>Port</Label>
              <Input type="number" value={s.smtp.port} onChange={e => setS({ ...s, smtp: { ...s.smtp, port: parseInt(e.target.value) || 587 } })}
                data-testid="smtp-port" />
            </div>
            <div>
              <Label>Username</Label>
              <Input value={s.smtp.username} onChange={e => setS({ ...s, smtp: { ...s.smtp, username: e.target.value } })}
                placeholder="your.email@gmail.com" autoComplete="username" data-testid="smtp-username" />
            </div>
            <div>
              <Label>Password</Label>
              <SecretInput
                value={s.smtp.password}
                onChange={(v) => setS({ ...s, smtp: { ...s.smtp, password: v } })}
                configured={!!s.smtp.password_set}
                testId="smtp-password"
                placeholder="App Password (16 chars for Gmail)"
              />
            </div>
            <div>
              <Label>Encryption</Label>
              <Select value={s.smtp.encryption} onValueChange={(v) => setS({ ...s, smtp: { ...s.smtp, encryption: v } })}>
                <SelectTrigger data-testid="smtp-encryption"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="tls">STARTTLS</SelectItem>
                  <SelectItem value="ssl">SSL</SelectItem>
                  <SelectItem value="none">None</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      )}

      {s.mode === 'EXTERNAL' && (
        <div className="bg-white border border-border rounded-lg p-5 space-y-4" data-testid="external-section">
          <h3 className="font-semibold">External Provider (API)</h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Provider</Label>
              <Select value={s.external.provider} onValueChange={(v) => setS({ ...s, external: { ...s.external, provider: v } })}>
                <SelectTrigger data-testid="ext-provider"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="brevo">Brevo (Sendinblue)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>API Key</Label>
              <SecretInput
                value={s.external.api_key}
                onChange={(v) => setS({ ...s, external: { ...s.external, api_key: v } })}
                configured={!!s.external.api_key_set}
                testId="ext-api-key"
                placeholder="Provider API key"
              />
            </div>
          </div>
        </div>
      )}

      <div className="flex items-center gap-3 flex-wrap">
        <Button onClick={save} disabled={saving} className="bg-primary text-white" data-testid="save-settings-btn">
          <FloppyDisk size={16} className="mr-2" weight="bold" /> {saving ? 'Saving…' : 'Save Settings'}
        </Button>
        <Button variant="outline" onClick={load} data-testid="reload-settings-btn">
          <ArrowClockwise size={16} className="mr-2" /> Reload
        </Button>
        {s.mode === 'INTERNAL' && (
          <Button
            variant="outline"
            onClick={runDiagnostics}
            disabled={diagRunning}
            data-testid="diagnostics-btn"
            className="border-info text-info hover:bg-info-soft"
          >
            <Plugs size={16} className="mr-2" weight="bold" /> {diagRunning ? 'Running…' : 'Test Connection'}
          </Button>
        )}
      </div>

      {diag && (
        <div
          className={`bg-white border rounded-lg p-5 ${diag.ok ? 'border-success' : 'border-danger'}`}
          data-testid="diagnostics-panel"
        >
          <div className="flex items-center gap-2 mb-3">
            {diag.ok
              ? <CheckCircle size={20} weight="fill" className="text-success" />
              : <XCircle size={20} weight="fill" className="text-danger" />}
            <h3 className="font-semibold" data-testid="diagnostics-summary">{diag.summary}</h3>
          </div>
          <ul className="space-y-2">
            {Object.entries(diag.stages).map(([key, st]) => (
              <li key={key} className="flex items-start gap-2.5" data-testid={`stage-${key}`}>
                <span className="mt-0.5">{STAGE_ICON[st.status] || STAGE_ICON.pending}</span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium">{STAGE_LABEL[key] || key}</span>
                    <Badge variant="outline" className="text-[10px] uppercase">{st.status}</Badge>
                  </div>
                  {st.detail && (
                    <p
                      className={`text-xs mt-0.5 whitespace-pre-wrap break-words ${st.status === 'fail' ? 'text-danger' : 'text-text_muted'}`}
                      data-testid={`stage-${key}-detail`}
                    >
                      {st.detail}
                    </p>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="bg-white border border-border rounded-lg p-5">
        <h3 className="font-semibold mb-3">Send Test Email</h3>
        <div className="flex gap-2">
          <Input value={testEmail} onChange={e => setTestEmail(e.target.value)}
            placeholder="recipient@example.com" type="email" data-testid="test-email-input" />
          <Button onClick={sendTest} disabled={testing} className="bg-primary text-white" data-testid="send-test-btn">
            <PaperPlaneRight size={16} className="mr-2" weight="bold" /> {testing ? 'Sending…' : 'Send Test'}
          </Button>
        </div>
      </div>
    </div>
  );
};

/* ──────────────────────────────────────────────────────────────
   Variable Browser — categorized one-click insert
   ────────────────────────────────────────────────────────────── */
const VARIABLE_CATEGORIES = [
  { key: 'user', label: 'User', vars: ['user_name', 'user_email'] },
  { key: 'listing', label: 'Listing', vars: ['listing_title', 'listing_link', 'listing_id', 'expiration_date'] },
  { key: 'auth', label: 'Auth', vars: ['reset_link'] },
  { key: 'messaging', label: 'Messaging', vars: ['sender_name', 'message_link'] },
  { key: 'commerce', label: 'Commerce', vars: ['amount', 'payment_description', 'payment_reference'] },
  { key: 'moderation', label: 'Moderation', vars: ['rejection_reason', 'suspension_reason', 'days_left'] },
  { key: 'system', label: 'System', vars: ['app_name', 'support_email'] },
];

const VariableBrowser = ({ eventType, onInsert }) => {
  const [eventVars, setEventVars] = useState([]);
  useEffect(() => {
    if (!eventType) return;
    axios.get(`${API}/admin/email-events`, { withCredentials: true })
      .then(({ data }) => {
        const evt = data.events.find((e) => e.type === eventType);
        setEventVars(evt?.variables || []);
      })
      .catch(() => {});
  }, [eventType]);

  return (
    <div className="md:col-span-1 bg-surface border border-border rounded-lg p-3 space-y-3" data-testid="variable-browser">
      <div>
        <p className="text-xs font-semibold uppercase text-text_muted mb-1">Available for {eventType}</p>
        <div className="flex flex-wrap gap-1.5" data-testid="vb-event-vars">
          {eventVars.length === 0 ? (
            <span className="text-xs text-text_muted">No specific variables documented for this event.</span>
          ) : eventVars.map((v) => (
            <button
              key={v}
              type="button"
              onClick={() => onInsert(`{{${v}}}`)}
              className="text-xs px-2 py-1 rounded bg-info-soft text-info border border-info/20 hover:bg-info/10 font-mono"
              data-testid={`vb-var-${v}`}
            >
              {`{{${v}}}`}
            </button>
          ))}
        </div>
      </div>
      <div className="border-t border-border pt-3 space-y-3">
        <p className="text-xs font-semibold uppercase text-text_muted">All variables</p>
        {VARIABLE_CATEGORIES.map((cat) => (
          <div key={cat.key}>
            <p className="text-xs font-medium text-text_main mb-1">{cat.label}</p>
            <div className="flex flex-wrap gap-1.5">
              {cat.vars.map((v) => (
                <button
                  key={v}
                  type="button"
                  onClick={() => onInsert(`{{${v}}}`)}
                  className="text-xs px-2 py-0.5 rounded bg-white text-text_main border border-border hover:border-primary hover:text-primary font-mono"
                  data-testid={`vb-cat-${cat.key}-${v}`}
                >
                  {`{{${v}}}`}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};


/* ──────────────────────────────────────────────────────────────
   TAB 2 — Templates
   ────────────────────────────────────────────────────────────── */
const LANG_CODES = ['fr', 'en', 'mg'];
const LANG_LABELS = { fr: 'Français', en: 'English', mg: 'Malagasy' };
const LANG_FLAGS  = { fr: '🇫🇷', en: '🇬🇧', mg: '🇲🇬' };

const EmailTemplatesPanel = () => {
  const [list, setList] = useState([]);
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  // editing = { type, variants: {fr, en, mg}, activeLang }
  const [editing, setEditing] = useState(null);
  const [preview, setPreview] = useState(null);  // {subject, body_html}

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/email-templates`, { withCredentials: true });
      setList(data.templates); setTypes(data.types);
    } catch { toast.error('Failed to load templates'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  // Group the flat list by `type` — each group carries its 3 language
  // variants (some may be missing shells that we render as blanks).
  const grouped = useMemo(() => {
    const byType = {};
    for (const t of list) {
      if (!byType[t.type]) byType[t.type] = { type: t.type, name: t.name, variants: {} };
      const lang = (t.language || 'fr').toLowerCase();
      byType[t.type].variants[lang] = t;
      // Prefer the display name from any language variant (usually similar).
      if (!byType[t.type].name || byType[t.type].name === t.type) {
        byType[t.type].name = t.name;
      }
    }
    return Object.values(byType).sort((a, b) => a.type.localeCompare(b.type));
  }, [list]);

  const openEditor = (group) => {
    // Fill in blank shells for any missing language so the editor UI is
    // always uniform (3 tabs, some potentially empty).
    const variants = {};
    for (const code of LANG_CODES) {
      const existing = group.variants[code];
      variants[code] = existing || {
        type: group.type,
        language: code,
        name: `${group.type} (${code.toUpperCase()})`,
        subject: '',
        body_html: '',
        body_text: '',
        active: false,
      };
    }
    setEditing({ type: group.type, variants, activeLang: 'fr' });
  };

  const openNew = (ty) => {
    const variants = {};
    for (const code of LANG_CODES) {
      variants[code] = {
        type: ty,
        language: code,
        name: `${ty} (${code.toUpperCase()})`,
        subject: '',
        body_html: '',
        body_text: '',
        active: code === 'fr',
      };
    }
    setEditing({ type: ty, variants, activeLang: 'fr' });
  };

  const current = editing?.variants?.[editing?.activeLang];
  const updateCurrent = (patch) => {
    if (!editing) return;
    setEditing({
      ...editing,
      variants: { ...editing.variants, [editing.activeLang]: { ...current, ...patch } },
    });
  };

  const save = async () => {
    if (!editing) return;
    try {
      // Save each language variant one by one. Skip empty shells that
      // the admin never filled in (subject + body both blank).
      for (const code of LANG_CODES) {
        const v = editing.variants[code];
        const filled = (v.subject || '').trim() || (v.body_html || '').trim();
        const alreadyExists = !!v.template_id;
        if (!filled && !alreadyExists) continue;  // nothing to persist yet
        const payload = {
          type: v.type,
          language: v.language,
          name: v.name || `${v.type} (${code.toUpperCase()})`,
          subject: v.subject || '',
          body_html: v.body_html || '',
          body_text: v.body_text || '',
          active: !!v.active,
        };
        if (alreadyExists) {
          await axios.put(`${API}/admin/email-templates/${v.template_id}`, payload, { withCredentials: true });
        } else {
          await axios.post(`${API}/admin/email-templates`, payload, { withCredentials: true });
        }
      }
      toast.success('Template saved (all languages)');
      setEditing(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
  };

  const del = async (group) => {
    if (!window.confirm(`Delete ALL ${LANG_CODES.length} language variants of "${group.type}"?`)) return;
    try {
      for (const v of Object.values(group.variants)) {
        if (v.template_id) {
          await axios.delete(`${API}/admin/email-templates/${v.template_id}`, { withCredentials: true });
        }
      }
      toast.success('Deleted'); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
  };

  const doPreview = async () => {
    if (!current) return;
    try {
      const { data } = await axios.post(`${API}/admin/email-templates/preview`,
        { subject: current.subject, body_html: current.body_html }, { withCredentials: true });
      setPreview(data);
    } catch { toast.error('Preview failed'); }
  };

  const doTestSend = async () => {
    if (!current) return;
    const to = window.prompt(`Send ${editing.activeLang.toUpperCase()} version to which email?`);
    if (!to) return;
    try {
      const { data } = await axios.post(`${API}/admin/email-templates/test-send`,
        { to_email: to, subject: current.subject, body_html: current.body_html }, { withCredentials: true });
      toast.success(data.message);
    } catch (e) { toast.error(e.response?.data?.detail || 'Test send failed'); }
  };

  const existingTypes = grouped.map(g => g.type);
  const addableTypes = types.filter(ty => !existingTypes.includes(ty));

  return (
    <div data-testid="email-templates-panel">
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-text_muted">Each template has 3 language variants (FR / EN / MG). The system picks the recipient's preferred language automatically, falling back to FR.</p>
        {addableTypes.length > 0 && (
          <Select onValueChange={openNew}>
            <SelectTrigger className="w-56" data-testid="add-template-select"><SelectValue placeholder="+ Add template for..." /></SelectTrigger>
            <SelectContent>
              {addableTypes.map(ty => <SelectItem key={ty} value={ty}>{ty}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
      </div>

      {loading ? <p className="text-text_muted">Loading…</p> : (
        <div className="space-y-2">
          {grouped.map(g => (
            <div key={g.type} className="bg-white border border-border rounded-lg p-4 flex items-center gap-3" data-testid={`template-row-${g.type}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge variant="outline" className="text-xs">{g.type}</Badge>
                  <span className="font-medium">{g.name}</span>
                  {/* Language status chips: green ✓ when filled+active, amber ⚠ when empty/inactive */}
                  {LANG_CODES.map(code => {
                    const v = g.variants[code];
                    const filled = v && v.active && (v.subject || '').trim() && (v.body_html || '').trim();
                    return (
                      <span
                        key={code}
                        className={`inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full border ${filled ? 'bg-success-soft text-success border-success/40' : 'bg-warning/10 text-warning border-warning/30'}`}
                        data-testid={`lang-status-${g.type}-${code}`}
                        title={filled ? `${LANG_LABELS[code]} filled` : `${LANG_LABELS[code]} empty`}
                      >
                        {LANG_FLAGS[code]} {code.toUpperCase()} {filled ? '✓' : '•'}
                      </span>
                    );
                  })}
                </div>
                <p className="text-xs text-text_muted mt-1 truncate">{g.variants.fr?.subject || g.variants.en?.subject || g.variants.mg?.subject || <em>No subject</em>}</p>
              </div>
              <Button variant="outline" size="sm" onClick={() => openEditor(g)} data-testid={`edit-${g.type}`}>
                <PencilSimple size={14} />
              </Button>
              <Button variant="outline" size="sm" onClick={() => del(g)} className="text-danger hover:bg-danger-soft" data-testid={`delete-${g.type}`}>
                <Trash size={14} />
              </Button>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto" data-testid="template-editor">
          <DialogHeader>
            <DialogTitle>{editing?.variants?.fr?.template_id ? 'Edit Template' : 'New Template'} — {editing?.type}</DialogTitle>
            <DialogDescription>Fill each language tab. Empty tabs are ignored on save. Use {'{{variables}}'} for dynamic values.</DialogDescription>
          </DialogHeader>

          {editing && (
            <>
              {/* Language tab bar */}
              <div className="flex gap-2 mb-4 border-b border-border" data-testid="tmpl-lang-tabs">
                {LANG_CODES.map(code => {
                  const v = editing.variants[code];
                  const filled = v && (v.subject || '').trim() && (v.body_html || '').trim();
                  const active = editing.activeLang === code;
                  return (
                    <button
                      key={code}
                      type="button"
                      onClick={() => setEditing({ ...editing, activeLang: code })}
                      className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px flex items-center gap-2 transition-colors ${active ? 'border-primary text-primary' : 'border-transparent text-text_muted hover:text-text_main'}`}
                      data-testid={`tmpl-lang-tab-${code}`}
                    >
                      <span>{LANG_FLAGS[code]}</span>
                      <span>{LANG_LABELS[code]}</span>
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${filled ? 'bg-success-soft text-success' : 'bg-warning/20 text-warning'}`}>
                        {filled ? '✓' : 'empty'}
                      </span>
                    </button>
                  );
                })}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 py-2">
                <div className="md:col-span-2 space-y-3">
                  <div>
                    <Label>Name</Label>
                    <Input value={current?.name || ''} onChange={e => updateCurrent({ name: e.target.value })} data-testid="tmpl-name" />
                  </div>
                  <div>
                    <Label>Subject ({editing.activeLang.toUpperCase()})</Label>
                    <Input
                      value={current?.subject || ''} id="tmpl-subject-input"
                      onChange={e => updateCurrent({ subject: e.target.value })}
                      data-testid="tmpl-subject"
                    />
                  </div>
                  <div>
                    <Label>HTML Body ({editing.activeLang.toUpperCase()})</Label>
                    <textarea value={current?.body_html || ''} id="tmpl-body-input"
                      onChange={e => updateCurrent({ body_html: e.target.value })}
                      rows={14} className="w-full border border-border rounded px-3 py-2 text-xs font-mono"
                      data-testid="tmpl-body" />
                  </div>
                  <div>
                    <Label>Plain Text Body ({editing.activeLang.toUpperCase()}) <span className="text-[11px] text-text_muted">(optional fallback)</span></Label>
                    <textarea value={current?.body_text || ''}
                      onChange={e => updateCurrent({ body_text: e.target.value })}
                      rows={5} className="w-full border border-border rounded px-3 py-2 text-xs font-mono"
                      data-testid="tmpl-body-text" />
                  </div>
                  <div className="flex items-center gap-2">
                    <input type="checkbox" id="tmpl-active" checked={!!current?.active}
                      onChange={e => updateCurrent({ active: e.target.checked })} data-testid="tmpl-active" />
                    <Label htmlFor="tmpl-active" className="cursor-pointer">Active ({editing.activeLang.toUpperCase()})</Label>
                  </div>
                </div>

                <VariableBrowser
                  eventType={editing.type}
                  onInsert={(token) => {
                    const active = document.activeElement;
                    const target = active && active.id === 'tmpl-subject-input' ? 'subject' : 'body_html';
                    const cur = current?.[target] || '';
                    const start = active && active.id === 'tmpl-subject-input' ? active.selectionStart : null;
                    if (start != null) {
                      const next = cur.slice(0, start) + token + cur.slice(active.selectionEnd);
                      updateCurrent({ [target]: next });
                    } else {
                      updateCurrent({ [target]: cur + token });
                    }
                  }}
                />
              </div>
            </>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={doPreview} data-testid="tmpl-preview-btn"><Eye size={14} className="mr-1.5" /> Preview {editing?.activeLang?.toUpperCase()}</Button>
            <Button variant="outline" onClick={doTestSend} data-testid="tmpl-test-btn"><PaperPlaneRight size={14} className="mr-1.5" /> Send Test {editing?.activeLang?.toUpperCase()}</Button>
            <Button variant="outline" onClick={() => setEditing(null)} data-testid="tmpl-cancel-btn">Cancel</Button>
            <Button onClick={save} className="bg-primary text-white" data-testid="tmpl-save-btn">Save All Languages</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="tmpl-preview-modal">
          <DialogHeader>
            <DialogTitle>Preview: {preview?.subject}</DialogTitle>
            <DialogDescription>Rendered with sample variables.</DialogDescription>
          </DialogHeader>
          <div className="bg-surface border border-border rounded p-4">
            <iframe srcDoc={preview?.body_html} title="preview" className="w-full h-[400px] bg-white border border-border rounded" sandbox="" />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

/* ──────────────────────────────────────────────────────────────
   TAB 3 — Notification Logs
   ────────────────────────────────────────────────────────────── */
const NotificationLogsPanel = () => {
  const platformTz = usePlatformTimezone();
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ notification_type: '', channel: '', status: '', start_date: '', end_date: '' });
  const [page, setPage] = useState(1);

  const load = async () => {
    setLoading(true);
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v) params.append(k, v); });
    params.append('page', page); params.append('limit', 50);
    try {
      const { data } = await axios.get(`${API}/admin/notification-logs?${params}`, { withCredentials: true });
      setLogs(data.logs); setTotal(data.total); setTypes(data.available_types);
    } catch { toast.error('Failed to load logs'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [page]);  // eslint-disable-line

  const apply = () => { setPage(1); load(); };
  const reset = () => { setFilters({ notification_type: '', channel: '', status: '', start_date: '', end_date: '' }); setPage(1); setTimeout(load, 0); };

  return (
    <div data-testid="notification-logs-panel">
      <div className="bg-white border border-border rounded-lg p-4 mb-4">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <div>
            <Label className="text-xs">Type</Label>
            <Select value={filters.notification_type || '_all'} onValueChange={(v) => setFilters({ ...filters, notification_type: v === '_all' ? '' : v })}>
              <SelectTrigger data-testid="filter-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="_all">All types</SelectItem>
                {types.map(ty => <SelectItem key={ty} value={ty}>{ty}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Channel</Label>
            <Select value={filters.channel || '_all'} onValueChange={(v) => setFilters({ ...filters, channel: v === '_all' ? '' : v })}>
              <SelectTrigger data-testid="filter-channel"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="_all">All channels</SelectItem>
                <SelectItem value="email">Email</SelectItem>
                <SelectItem value="push">Push</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Status</Label>
            <Select value={filters.status || '_all'} onValueChange={(v) => setFilters({ ...filters, status: v === '_all' ? '' : v })}>
              <SelectTrigger data-testid="filter-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="_all">All statuses</SelectItem>
                <SelectItem value="sent">Sent</SelectItem>
                <SelectItem value="failed">Failed</SelectItem>
                <SelectItem value="stubbed">Stubbed</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">From</Label>
            <Input type="date" value={filters.start_date} onChange={e => setFilters({ ...filters, start_date: e.target.value })} data-testid="filter-start" />
          </div>
          <div>
            <Label className="text-xs">To</Label>
            <Input type="date" value={filters.end_date} onChange={e => setFilters({ ...filters, end_date: e.target.value })} data-testid="filter-end" />
          </div>
        </div>
        <div className="flex gap-2 mt-3">
          <Button onClick={apply} className="bg-primary text-white" data-testid="apply-filters">Apply</Button>
          <Button variant="outline" onClick={reset} data-testid="reset-filters">Reset</Button>
          <span className="text-sm text-text_muted self-center ml-auto">Total: <strong>{total}</strong></span>
        </div>
      </div>

      {loading ? <p className="text-text_muted">Loading…</p> : logs.length === 0 ? (
        <div className="bg-white border border-border rounded-lg p-8 text-center" data-testid="no-logs">
          <p className="text-text_muted">No notification logs yet.</p>
        </div>
      ) : (
        <div className="bg-white border border-border rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-surface text-xs uppercase tracking-wider text-text_muted">
              <tr>
                <th className="px-4 py-3 text-left">Date</th>
                <th className="px-4 py-3 text-left">Type</th>
                <th className="px-4 py-3 text-left">Channel</th>
                <th className="px-4 py-3 text-left">Status</th>
                <th className="px-4 py-3 text-left">Recipient</th>
                <th className="px-4 py-3 text-left">Provider / Error</th>
              </tr>
            </thead>
            <tbody>
              {logs.map(l => (
                <tr key={l.log_id} className="border-t border-border" data-testid={`log-row-${l.log_id}`}>
                  <td className="px-4 py-2 text-xs">{formatInTimezone(l.created_at, platformTz, { second: '2-digit' })}</td>
                  <td className="px-4 py-2"><Badge variant="outline">{l.notification_type}</Badge></td>
                  <td className="px-4 py-2">{l.channel}</td>
                  <td className="px-4 py-2">
                    <Badge className={l.status === 'sent' ? 'bg-success' : l.status === 'failed' ? 'bg-danger' : 'bg-warning'}>
                      {l.status}
                    </Badge>
                  </td>
                  <td className="px-4 py-2 text-xs">{l.to_email || '—'}</td>
                  <td className="px-4 py-2 text-xs text-text_muted truncate max-w-[240px]">{l.error || l.provider || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};


/* ──────────────────────────────────────────────────────────────
   TAB — Dashboard
   ────────────────────────────────────────────────────────────── */
const STATUS_TILES = [
  { key: 'pending',   label: 'Pending',   color: 'text-warning',  bg: 'bg-warning-soft border-warning/20' },
  { key: 'scheduled', label: 'Scheduled', color: 'text-info',     bg: 'bg-info-soft border-info/20' },
  { key: 'sending',   label: 'Sending',   color: 'text-info',     bg: 'bg-info-soft border-info/20' },
  { key: 'sent',      label: 'Sent',      color: 'text-success',  bg: 'bg-success-soft border-success/20' },
  { key: 'failed',    label: 'Failed',    color: 'text-danger',   bg: 'bg-danger-soft border-danger/20' },
  { key: 'cancelled', label: 'Cancelled', color: 'text-text_muted', bg: 'bg-surface border-border' },
];

const EmailDashboardPanel = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/email-queue/stats`, { withCredentials: true });
      setStats(data);
    } catch { toast.error('Failed to load stats'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); const i = setInterval(load, 15000); return () => clearInterval(i); }, []);

  if (loading || !stats) return <p className="text-text_muted">Loading…</p>;
  const total = stats.total || 0;
  const pct = (n) => (total ? Math.round((n / total) * 100) : 0);

  return (
    <div data-testid="email-dashboard-panel" className="space-y-5">
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
        {STATUS_TILES.map((t) => (
          <div
            key={t.key}
            className={`border rounded-lg p-4 ${t.bg}`}
            data-testid={`stat-${t.key}`}
          >
            <p className="text-xs uppercase tracking-wide text-text_muted">{t.label}</p>
            <p className={`text-3xl font-bold mt-1 ${t.color}`} data-testid={`stat-${t.key}-count`}>
              {stats[t.key] ?? 0}
            </p>
            <p className="text-xs text-text_muted mt-1" data-testid={`stat-${t.key}-pct`}>
              {pct(stats[t.key] ?? 0)}% du total
            </p>
          </div>
        ))}
      </div>
      <div className="bg-white border border-border rounded-lg p-4 flex items-center justify-between">
        <div>
          <p className="text-sm font-semibold">Total queue rows</p>
          <p className="text-2xl font-bold" data-testid="stat-total-count">{total}</p>
        </div>
        <Button variant="outline" onClick={load} data-testid="dashboard-refresh-btn">
          <ArrowClockwise size={14} className="mr-2" /> Refresh
        </Button>
      </div>
    </div>
  );
};


/* ──────────────────────────────────────────────────────────────
   Shared bits — status badge, queue row details drawer
   ────────────────────────────────────────────────────────────── */
const STATUS_BADGE_CLASS = {
  pending:   'bg-warning-soft text-warning border border-warning/30',
  scheduled: 'bg-info-soft text-info border border-info/30',
  sending:   'bg-info-soft text-info border border-info/30',
  sent:      'bg-success-soft text-success border border-success/30',
  failed:    'bg-danger-soft text-danger border border-danger/30',
  cancelled: 'bg-surface text-text_muted border border-border',
};
const StatusBadge = ({ status }) => (
  <span
    className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase ${STATUS_BADGE_CLASS[status] || 'bg-surface text-text_muted border border-border'}`}
    data-testid={`status-badge-${status}`}
  >
    {status}
  </span>
);


const QueueDetailsDrawer = ({ queueId, onClose, onMutated }) => {
  const platformTz = usePlatformTimezone();
  const [row, setRow] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    if (!queueId) { setRow(null); return; }
    let alive = true;
    setLoading(true);
    axios.get(`${API}/admin/email-queue/${queueId}`, { withCredentials: true })
      .then(({ data }) => { if (alive) setRow(data); })
      .catch(() => toast.error('Failed to load queue row'))
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [queueId]);

  const doCancel = async () => {
    try {
      await axios.post(`${API}/admin/email-queue/${queueId}/cancel`, {}, { withCredentials: true });
      toast.success('Cancelled'); onMutated?.(); onClose?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Cancel failed'); }
  };
  const doRetry = async () => {
    try {
      await axios.post(`${API}/admin/email-queue/${queueId}/retry`, {}, { withCredentials: true });
      toast.success('Re-queued'); onMutated?.(); onClose?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Retry failed'); }
  };

  return (
    <Dialog open={!!queueId} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="queue-details-drawer">
        <DialogHeader>
          <DialogTitle>Queue item details</DialogTitle>
          <DialogDescription>Full event timeline + delivery context.</DialogDescription>
        </DialogHeader>
        {loading || !row ? <p className="text-text_muted">Loading…</p> : (
          <div className="space-y-4 text-sm">
            <div className="grid grid-cols-2 gap-x-4 gap-y-2">
              <div><span className="text-text_muted">queue_id:</span> <code className="text-xs">{row.queue_id}</code></div>
              <div><span className="text-text_muted">status:</span> <StatusBadge status={row.status} /></div>
              <div><span className="text-text_muted">recipient:</span> {row.to_email}</div>
              <div><span className="text-text_muted">template:</span> {row.template_type}</div>
              <div><span className="text-text_muted">attempts:</span> {row.attempts} / {row.max_attempts}</div>
              <div><span className="text-text_muted">provider:</span> {row.provider_used || '—'}</div>
              <div><span className="text-text_muted">scheduled_at:</span> {formatInTimezone(row.scheduled_at, platformTz, { second: '2-digit' }) || '—'}</div>
              <div><span className="text-text_muted">sent_at:</span> {formatInTimezone(row.sent_at, platformTz, { second: '2-digit' }) || '—'}</div>
              <div><span className="text-text_muted">campaign_id:</span> {row.campaign_id || '—'}</div>
              <div><span className="text-text_muted">created_by:</span> {row.created_by}</div>
            </div>

            {row.last_error && (
              <div className="bg-danger-soft border border-danger/30 rounded-md p-3 text-danger" data-testid="row-last-error">
                <p className="font-semibold text-xs uppercase mb-1">SMTP / Provider error</p>
                <p className="text-xs whitespace-pre-wrap break-words">{row.last_error}</p>
              </div>
            )}

            <div>
              <p className="font-semibold mb-2 flex items-center gap-2"><Code size={14} /> Variables used</p>
              <pre className="bg-surface border border-border rounded-md p-3 text-xs overflow-x-auto" data-testid="row-variables">
                {JSON.stringify(row.variables, null, 2)}
              </pre>
            </div>

            <div>
              <p className="font-semibold mb-2 flex items-center gap-2"><Clock size={14} /> Event timeline</p>
              <ol className="space-y-2 border-l-2 border-border pl-4" data-testid="row-history">
                {(row.history || []).map((h, i) => (
                  <li key={i} className="relative">
                    <span className="absolute -left-[1.18rem] top-1.5 w-2.5 h-2.5 rounded-full bg-primary" />
                    <div className="flex items-baseline gap-2 flex-wrap">
                      <StatusBadge status={h.status} />
                      <span className="text-xs text-text_muted">{formatInTimezone(h.at, platformTz, { second: '2-digit' })}</span>
                    </div>
                    <p className="text-xs mt-0.5">{h.note}</p>
                    {h.error && <p className="text-xs text-danger mt-0.5 break-words">{h.error}</p>}
                    {h.next_retry_at && <p className="text-xs text-text_muted mt-0.5">↪ next retry at {formatInTimezone(h.next_retry_at, platformTz, { second: '2-digit' })}</p>}
                  </li>
                ))}
              </ol>
            </div>
          </div>
        )}
        <DialogFooter className="flex items-center gap-2">
          {row && row.status !== 'sent' && row.status !== 'cancelled' && (
            <Button variant="outline" onClick={doCancel} data-testid="row-cancel-btn">
              <Prohibit size={14} className="mr-2" /> Cancel
            </Button>
          )}
          {row && row.status === 'failed' && (
            <Button variant="outline" onClick={doRetry} data-testid="row-retry-btn">
              <ArrowsClockwise size={14} className="mr-2" /> Retry now
            </Button>
          )}
          <Button onClick={onClose} className="bg-primary text-white">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};


/* ──────────────────────────────────────────────────────────────
   TAB — Queue
   ────────────────────────────────────────────────────────────── */
const EmailQueuePanel = () => {
  const platformTz = usePlatformTimezone();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    status: '', template_type: '', recipient: '', date_from: '', date_to: '',
  });
  const [page, setPage] = useState(1);
  const [openId, setOpenId] = useState(null);
  const [events, setEvents] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const params = { page, limit: 50 };
      for (const [k, v] of Object.entries(filters)) if (v) params[k] = v;
      const { data } = await axios.get(`${API}/admin/email-queue`, { params, withCredentials: true });
      setRows(data.items); setTotal(data.total);
    } catch { toast.error('Failed to load queue'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [page, filters.status, filters.template_type]);
  useEffect(() => {
    axios.get(`${API}/admin/email-events`, { withCredentials: true })
      .then(({ data }) => setEvents(data.events))
      .catch(() => {});
  }, []);

  const applyFilters = (e) => { e?.preventDefault?.(); setPage(1); load(); };
  const clearFilters = () => { setFilters({ status: '', template_type: '', recipient: '', date_from: '', date_to: '' }); setPage(1); };

  return (
    <div data-testid="email-queue-panel" className="space-y-4">
      <form
        onSubmit={applyFilters}
        className="bg-white border border-border rounded-lg p-4 grid grid-cols-2 md:grid-cols-5 gap-3 items-end"
      >
        <div>
          <Label className="text-xs">Recipient</Label>
          <Input
            value={filters.recipient}
            onChange={(e) => setFilters({ ...filters, recipient: e.target.value })}
            placeholder="email contains…"
            data-testid="queue-filter-recipient"
          />
        </div>
        <div>
          <Label className="text-xs">Status</Label>
          <Select value={filters.status || 'all'} onValueChange={(v) => setFilters({ ...filters, status: v === 'all' ? '' : v })}>
            <SelectTrigger data-testid="queue-filter-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              {STATUS_TILES.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Template</Label>
          <Select value={filters.template_type || 'all'} onValueChange={(v) => setFilters({ ...filters, template_type: v === 'all' ? '' : v })}>
            <SelectTrigger data-testid="queue-filter-template"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              {events.map((e) => <SelectItem key={e.type} value={e.type}>{e.type}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">From (ISO date)</Label>
          <Input
            value={filters.date_from}
            onChange={(e) => setFilters({ ...filters, date_from: e.target.value })}
            placeholder="2026-01-01"
            data-testid="queue-filter-from"
          />
        </div>
        <div>
          <Label className="text-xs">To (ISO date)</Label>
          <Input
            value={filters.date_to}
            onChange={(e) => setFilters({ ...filters, date_to: e.target.value })}
            placeholder="2026-12-31"
            data-testid="queue-filter-to"
          />
        </div>
        <div className="flex items-center gap-2 md:col-span-5">
          <Button type="submit" className="bg-primary text-white" data-testid="queue-apply-filters">
            <MagnifyingGlass size={14} className="mr-2" /> Search
          </Button>
          <Button type="button" variant="outline" onClick={clearFilters} data-testid="queue-clear-filters">Clear</Button>
          <span className="ml-auto text-xs text-text_muted">{total} row(s) match</span>
        </div>
      </form>

      <div className="bg-white border border-border rounded-lg overflow-x-auto" data-testid="queue-table-wrap">
        <table className="min-w-full text-sm">
          <thead className="bg-surface text-text_muted text-xs uppercase">
            <tr>
              <th className="px-4 py-2 text-left">Created</th>
              <th className="px-4 py-2 text-left">Template</th>
              <th className="px-4 py-2 text-left">Recipient</th>
              <th className="px-4 py-2 text-left">Status</th>
              <th className="px-4 py-2 text-left">Attempts</th>
              <th className="px-4 py-2 text-left">Campaign</th>
              <th className="px-4 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="px-4 py-6 text-center text-text_muted">Loading…</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={7} className="px-4 py-6 text-center text-text_muted">No rows match the filters.</td></tr>
            ) : rows.map((r) => (
              <tr
                key={r.queue_id}
                className="border-t border-border hover:bg-surface cursor-pointer"
                onClick={() => setOpenId(r.queue_id)}
                data-testid={`queue-row-${r.queue_id}`}
              >
                <td className="px-4 py-2 text-xs">{formatInTimezone(r.created_at, platformTz, { second: '2-digit' })}</td>
                <td className="px-4 py-2"><Badge variant="outline" className="text-xs">{r.template_type}</Badge></td>
                <td className="px-4 py-2 text-xs">{r.to_email}</td>
                <td className="px-4 py-2"><StatusBadge status={r.status} /></td>
                <td className="px-4 py-2 text-xs">{r.attempts}/{r.max_attempts}</td>
                <td className="px-4 py-2 text-xs">{r.campaign_id || '—'}</td>
                <td className="px-4 py-2 text-right"><CaretRight size={14} className="text-text_muted" /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex items-center gap-2 justify-end">
        <Button variant="outline" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="queue-prev">Prev</Button>
        <span className="text-xs text-text_muted">Page {page}</span>
        <Button variant="outline" disabled={rows.length < 50} onClick={() => setPage(page + 1)} data-testid="queue-next">Next</Button>
      </div>

      <QueueDetailsDrawer queueId={openId} onClose={() => setOpenId(null)} onMutated={load} />
    </div>
  );
};


/* ──────────────────────────────────────────────────────────────
   TAB — Campaigns (create + history)
   ────────────────────────────────────────────────────────────── */
const CAMPAIGN_STATUS_BADGE = {
  draft:     'bg-surface text-text_muted border border-border',
  scheduled: 'bg-info-soft text-info border border-info/30',
  running:   'bg-warning-soft text-warning border border-warning/30',
  completed: 'bg-success-soft text-success border border-success/30',
  cancelled: 'bg-surface text-text_muted border border-border',
};

const PreviewPane = ({ preview }) => {
  const [device, setDevice] = useState('desktop');
  if (!preview) return null;
  const width = device === 'desktop' ? 560 : 320;
  return (
    <div className="bg-white border border-border rounded-lg p-3 space-y-2" data-testid="preview-pane">
      <div className="flex items-center gap-2">
        <Button
          size="sm" variant={device === 'desktop' ? 'default' : 'outline'}
          onClick={() => setDevice('desktop')} data-testid="preview-desktop-btn"
          className={device === 'desktop' ? 'bg-primary text-white' : ''}
        ><DesktopTower size={14} className="mr-2" /> Desktop</Button>
        <Button
          size="sm" variant={device === 'mobile' ? 'default' : 'outline'}
          onClick={() => setDevice('mobile')} data-testid="preview-mobile-btn"
          className={device === 'mobile' ? 'bg-primary text-white' : ''}
        ><DeviceMobile size={14} className="mr-2" /> Mobile</Button>
      </div>
      <div>
        <p className="text-xs text-text_muted">Subject</p>
        <p className="text-sm font-medium" data-testid="preview-subject">{preview.subject}</p>
      </div>
      <div className="mx-auto bg-surface p-3 rounded-md overflow-auto" style={{ width, maxWidth: '100%' }}>
        <div
          className="bg-white rounded-md shadow-sm"
          dangerouslySetInnerHTML={{ __html: preview.body_html }}
          data-testid="preview-body"
        />
      </div>
    </div>
  );
};


const EmailCampaignsPanel = () => {
  const platformTz = usePlatformTimezone();
  const [campaigns, setCampaigns] = useState([]);
  const [events, setEvents] = useState([]);
  const [senders, setSenders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [openCamp, setOpenCamp] = useState(null);

  // Form state
  const [form, setForm] = useState({
    name: '', template_type: '', sender_id: '',
    audience: 'all_users', role: 'user', emails: '', registered_after: '',
    region: '', category_slug: '',
    scheduled_at: '',
  });
  const [regions, setRegions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [editingId, setEditingId] = useState(null);   // camp id being edited
  const [estimate, setEstimate] = useState(null);
  const [preview, setPreview] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const loadCampaigns = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/email-campaigns`, { withCredentials: true });
      setCampaigns(data.items);
    } catch { toast.error('Failed to load campaigns'); }
    finally { setLoading(false); }
  };
  useEffect(() => {
    loadCampaigns();
    axios.get(`${API}/admin/email-events`, { withCredentials: true })
      .then(({ data }) => setEvents(data.events))
      .catch(() => {});
    axios.get(`${API}/admin/email-senders`, { withCredentials: true })
      .then(({ data }) => setSenders(data.items || []))
      .catch(() => {});
    // Regions + categories for the two new audience types.
    axios.get(`${API}/regions`)
      .then(({ data }) => setRegions((data.regions || data.items || data || []).filter(Boolean)))
      .catch(() => {});
    axios.get(`${API}/categories`)
      .then(({ data }) => setCategories(Array.isArray(data) ? data : (data.categories || data.items || [])))
      .catch(() => {});
  }, []);

  const audienceBody = () => {
    const body = { audience: form.audience };
    if (form.audience === 'by_role') body.role = form.role;
    if (form.audience === 'by_email_list') {
      body.emails = form.emails.split(/[\s,;]+/).map((e) => e.trim()).filter(Boolean);
    }
    if (form.audience === 'by_registration_after' && form.registered_after) {
      body.registered_after = new Date(form.registered_after).toISOString();
    }
    if (form.audience === 'by_region' && form.region) body.region = form.region;
    if (form.audience === 'by_active_listing_category' && form.category_slug) {
      body.category_slug = form.category_slug;
    }
    return body;
  };

  const doEstimate = async () => {
    try {
      const { data } = await axios.post(`${API}/admin/email-campaigns/estimate`,
        audienceBody(), { withCredentials: true });
      setEstimate(data);
      if (data.severity === 'warning') {
        toast.warning(`${data.recipient_count} recipients — large send, please review.`);
      }
    } catch (e) { toast.error(e.response?.data?.detail || 'Estimate failed'); }
  };

  const doPreview = async () => {
    if (!form.template_type) { toast.error('Pick a template first'); return; }
    try {
      const { data } = await axios.post(`${API}/admin/email-events/preview`,
        { template_type: form.template_type }, { withCredentials: true });
      setPreview(data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Preview failed'); }
  };

  const resetForm = () => {
    setEditingId(null);
    setEstimate(null); setPreview(null); setConfirmOpen(false);
    setForm({
      name: '', template_type: '', sender_id: '', audience: 'all_users',
      role: 'user', emails: '', registered_after: '', region: '', category_slug: '',
      scheduled_at: '',
    });
  };

  const submitCampaign = async (skipConfirm = false) => {
    if (!form.name || !form.template_type) { toast.error('Name + template required'); return; }
    // Edit mode: PUT instead of POST, no audience recompute.
    if (editingId) {
      setSubmitting(true);
      try {
        const body = {
          name: form.name,
          template_type: form.template_type,
          sender_id: form.sender_id || null,
        };
        if (form.scheduled_at) body.scheduled_at = zonedTimeToUtcIso(form.scheduled_at, platformTz);
        await axios.put(`${API}/admin/email-campaigns/${editingId}`, body, { withCredentials: true });
        toast.success('Campaign updated');
        setShowForm(false);
        resetForm();
        loadCampaigns();
      } catch (e) { toast.error(e.response?.data?.detail || 'Update failed'); }
      finally { setSubmitting(false); }
      return;
    }
    if (!estimate) { await doEstimate(); return; }
    // Confirmation gate for bulk sends.
    if (!skipConfirm && (form.audience === 'all_users' || estimate.severity === 'warning')) {
      setConfirmOpen(true);
      return;
    }
    setSubmitting(true);
    try {
      const body = {
        name: form.name,
        template_type: form.template_type,
        ...audienceBody(),
      };
      if (form.sender_id) body.sender_id = form.sender_id;
      if (form.scheduled_at) body.scheduled_at = zonedTimeToUtcIso(form.scheduled_at, platformTz);
      const { data } = await axios.post(`${API}/admin/email-campaigns`, body, { withCredentials: true });
      toast.success(`Campaign created → ${data.recipient_count} recipients`);
      setShowForm(false);
      resetForm();
      loadCampaigns();
    } catch (e) { toast.error(e.response?.data?.detail || 'Campaign failed'); }
    finally { setSubmitting(false); }
  };

  const cancelCampaign = async (cid) => {
    if (!window.confirm('Cancel this campaign and all its non-terminal queue rows?')) return;
    try {
      const { data } = await axios.post(`${API}/admin/email-campaigns/${cid}/cancel`, {}, { withCredentials: true });
      toast.success(`Cancelled ${data.rows_cancelled} pending rows`);
      loadCampaigns();
    } catch (e) { toast.error(e.response?.data?.detail || 'Cancel failed'); }
  };

  const deleteCampaign = async (cid) => {
    if (!window.confirm('Permanently delete this campaign and all its queue rows? This cannot be undone.')) return;
    try {
      const { data } = await axios.delete(`${API}/admin/email-campaigns/${cid}`, { withCredentials: true });
      toast.success(`Deleted campaign (${data.queue_rows_deleted} queue rows removed)`);
      loadCampaigns();
    } catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
  };

  const duplicateCampaign = async (cid) => {
    try {
      const { data } = await axios.post(`${API}/admin/email-campaigns/${cid}/duplicate`, {}, { withCredentials: true });
      toast.success(`Duplicated → ${data.name}`);
      loadCampaigns();
    } catch (e) { toast.error(e.response?.data?.detail || 'Duplicate failed'); }
  };

  const startEdit = (c) => {
    // Prefill form from the campaign doc + open the form panel.
    setForm({
      name: c.name || '',
      template_type: c.template_type || '',
      sender_id: c.sender_id || '',
      audience: c.audience || 'all_users',
      role: c.role || 'user',
      emails: (c.emails || []).join(', '),
      registered_after: c.registered_after ? c.registered_after.slice(0, 16) : '',
      region: c.region || '',
      category_slug: c.category_slug || '',
      scheduled_at: c.scheduled_at ? utcIsoToZonedInputValue(c.scheduled_at, platformTz) : '',
    });
    setEditingId(c.campaign_id);
    setEstimate({ recipient_count: c.recipient_count, severity: 'ok' });
    setShowForm(true);
  };

  return (
    <div data-testid="email-campaigns-panel" className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-text_muted">Create + manage scheduled or one-shot email campaigns.</p>
        <Button
          onClick={() => { if (showForm) { setShowForm(false); resetForm(); } else { setShowForm(true); } }}
          className="bg-primary text-white"
          data-testid="campaign-toggle-form"
        >
          <Plus size={14} className="mr-2" /> {showForm ? 'Close form' : 'New campaign'}
        </Button>
      </div>

      {showForm && (
        <div className="bg-white border border-border rounded-lg p-5 space-y-4" data-testid="campaign-form">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Name</Label>
              <Input
                value={form.name}
                onChange={(e) => { setForm({ ...form, name: e.target.value }); setEstimate(null); }}
                placeholder="e.g. Newsletter Feb 2026"
                data-testid="campaign-name"
              />
            </div>
            <div>
              <Label>Template</Label>
              <Select value={form.template_type} onValueChange={(v) => { setForm({ ...form, template_type: v }); setPreview(null); }}>
                <SelectTrigger data-testid="campaign-template"><SelectValue placeholder="Select template…" /></SelectTrigger>
                <SelectContent>
                  {events.map((e) => <SelectItem key={e.type} value={e.type}>{e.type}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Audience</Label>
              <Select
                value={form.audience}
                onValueChange={(v) => { setForm({ ...form, audience: v }); setEstimate(null); }}
              >
                <SelectTrigger data-testid="campaign-audience"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all_users">All users</SelectItem>
                  <SelectItem value="by_role">By role</SelectItem>
                  <SelectItem value="by_email_list">Custom email list</SelectItem>
                  <SelectItem value="by_registration_after">Registered after date</SelectItem>
                  <SelectItem value="by_region">By region</SelectItem>
                  <SelectItem value="by_active_listing_category">By active listing category</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Sender (From address)</Label>
              <Select
                value={form.sender_id || '__default__'}
                onValueChange={(v) => setForm({ ...form, sender_id: v === '__default__' ? '' : v })}
              >
                <SelectTrigger data-testid="campaign-sender"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__default__">Default sender</SelectItem>
                  {senders.filter((s) => s.is_active).map((s) => (
                    <SelectItem key={s.sender_id} value={s.sender_id}>
                      {s.display_name} &lt;{s.email}&gt;{s.is_default ? ' · default' : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Schedule (leave blank to send now)</Label>
              <Input
                type="datetime-local" value={form.scheduled_at}
                onChange={(e) => setForm({ ...form, scheduled_at: e.target.value })}
                data-testid="campaign-schedule"
              />
              <p className="text-xs text-text_muted mt-1">Heure locale de la plateforme ({platformTz})</p>
            </div>
          </div>
          {form.audience === 'by_role' && (
            <div>
              <Label>Role</Label>
              <Select value={form.role} onValueChange={(v) => { setForm({ ...form, role: v }); setEstimate(null); }}>
                <SelectTrigger data-testid="campaign-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="user">user</SelectItem>
                  <SelectItem value="admin">admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
          {form.audience === 'by_email_list' && (
            <div>
              <Label>Emails (comma / newline separated)</Label>
              <Textarea
                value={form.emails}
                onChange={(e) => { setForm({ ...form, emails: e.target.value }); setEstimate(null); }}
                rows={4}
                placeholder="a@x.com, b@y.com"
                data-testid="campaign-emails"
              />
            </div>
          )}
          {form.audience === 'by_registration_after' && (
            <div>
              <Label>Registered after</Label>
              <Input
                type="datetime-local"
                value={form.registered_after}
                onChange={(e) => { setForm({ ...form, registered_after: e.target.value }); setEstimate(null); }}
                data-testid="campaign-registered-after"
              />
            </div>
          )}
          {form.audience === 'by_region' && (
            <div>
              <Label>Region</Label>
              <Select
                value={form.region}
                onValueChange={(v) => { setForm({ ...form, region: v }); setEstimate(null); }}
              >
                <SelectTrigger data-testid="campaign-region"><SelectValue placeholder="Select region…" /></SelectTrigger>
                <SelectContent>
                  {regions.map((r) => {
                    const val = r?.name || r?.slug || r?.id || r;
                    return <SelectItem key={val} value={val}>{val}</SelectItem>;
                  })}
                </SelectContent>
              </Select>
            </div>
          )}
          {form.audience === 'by_active_listing_category' && (
            <div>
              <Label>Category (with active listings)</Label>
              <Select
                value={form.category_slug}
                onValueChange={(v) => { setForm({ ...form, category_slug: v }); setEstimate(null); }}
              >
                <SelectTrigger data-testid="campaign-category"><SelectValue placeholder="Select category…" /></SelectTrigger>
                <SelectContent>
                  {categories.map((c) => {
                    const val = c?.value || c?.slug || c?.name || c;
                    const label = c?.name || c?.value || c?.slug || c;
                    return <SelectItem key={val} value={val}>{label}</SelectItem>;
                  })}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="flex items-center gap-2 flex-wrap pt-2 border-t border-border">
            <Button variant="outline" onClick={doEstimate} data-testid="campaign-estimate-btn">
              <Funnel size={14} className="mr-2" /> Estimate recipients
            </Button>
            <Button variant="outline" onClick={doPreview} data-testid="campaign-preview-btn">
              <Eye size={14} className="mr-2" /> Preview email
            </Button>
            <div className="ml-auto flex items-center gap-3">
              {estimate && (
                <span className="text-sm" data-testid="campaign-estimate-result">
                  <strong>{estimate.recipient_count}</strong> recipients
                  {estimate.severity !== 'ok' && (
                    <Badge className={`ml-2 ${estimate.severity === 'warning' ? 'bg-danger text-white' : 'bg-warning text-text_main'}`}>
                      {estimate.severity}
                    </Badge>
                  )}
                </span>
              )}
              <Button
                onClick={() => submitCampaign(false)}
                disabled={submitting}
                className="bg-primary text-white"
                data-testid="campaign-submit-btn"
              >
                <PaperPlaneRight size={14} className="mr-2" />
                {form.scheduled_at ? 'Schedule' : 'Send now'}
              </Button>
            </div>
          </div>

          {preview && <PreviewPane preview={preview} />}
        </div>
      )}

      {/* Confirmation dialog for bulk sends */}
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent data-testid="campaign-confirm-dialog">
          <DialogHeader>
            <DialogTitle>Confirm bulk send</DialogTitle>
            <DialogDescription>
              You&apos;re about to send <strong>{estimate?.recipient_count}</strong> emails ({form.template_type}).
              This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)} data-testid="campaign-confirm-cancel">Cancel</Button>
            <Button onClick={() => submitCampaign(true)} className="bg-danger text-white" data-testid="campaign-confirm-go">
              Yes, send to {estimate?.recipient_count}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Campaign history table */}
      <div className="bg-white border border-border rounded-lg overflow-x-auto" data-testid="campaigns-table">
        <table className="min-w-full text-sm">
          <thead className="bg-surface text-text_muted text-xs uppercase">
            <tr>
              <th className="px-4 py-2 text-left">Name</th>
              <th className="px-4 py-2 text-left">Template</th>
              <th className="px-4 py-2 text-left">Status</th>
              <th className="px-4 py-2 text-left">Recipients</th>
              <th className="px-4 py-2 text-left">Sent / Failed</th>
              <th className="px-4 py-2 text-left">Created</th>
              <th className="px-4 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="px-4 py-6 text-center text-text_muted">Loading…</td></tr>
            ) : campaigns.length === 0 ? (
              <tr><td colSpan={7} className="px-4 py-6 text-center text-text_muted">No campaigns yet.</td></tr>
            ) : campaigns.map((c) => {
              const sent = c.status_counts?.sent || 0;
              const failed = c.status_counts?.failed || 0;
              return (
                <tr key={c.campaign_id} className="border-t border-border" data-testid={`campaign-row-${c.campaign_id}`}>
                  <td className="px-4 py-2 font-medium">{c.name}</td>
                  <td className="px-4 py-2"><Badge variant="outline" className="text-xs">{c.template_type}</Badge></td>
                  <td className="px-4 py-2">
                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase ${CAMPAIGN_STATUS_BADGE[c.status] || CAMPAIGN_STATUS_BADGE.draft}`}
                      data-testid={`campaign-status-${c.campaign_id}`}
                    >
                      {c.status || 'running'}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-xs">{c.recipient_count}</td>
                  <td className="px-4 py-2 text-xs">
                    <span className="text-success">{sent}</span>
                    {' / '}
                    <span className="text-danger">{failed}</span>
                  </td>
                  <td className="px-4 py-2 text-xs">{formatInTimezone(c.created_at, platformTz, { second: '2-digit' })}</td>
                  <td className="px-4 py-2 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button size="sm" variant="outline" onClick={() => setOpenCamp(c)} data-testid={`campaign-view-${c.campaign_id}`} title="View details">
                        <ArrowSquareOut size={12} />
                      </Button>
                      {c.status === 'scheduled' && (
                        <Button size="sm" variant="outline" onClick={() => startEdit(c)} data-testid={`campaign-edit-${c.campaign_id}`} title="Edit (scheduled only)">
                          <PencilSimple size={12} />
                        </Button>
                      )}
                      <Button size="sm" variant="outline" onClick={() => duplicateCampaign(c.campaign_id)} data-testid={`campaign-duplicate-${c.campaign_id}`} title="Duplicate">
                        <Copy size={12} />
                      </Button>
                      {!['completed', 'cancelled'].includes(c.status) && (
                        <Button size="sm" variant="outline" onClick={() => cancelCampaign(c.campaign_id)} data-testid={`campaign-cancel-${c.campaign_id}`} title="Cancel">
                          <Prohibit size={12} />
                        </Button>
                      )}
                      {['completed', 'cancelled'].includes(c.status) && (
                        <Button size="sm" variant="outline" onClick={() => deleteCampaign(c.campaign_id)} className="text-danger" data-testid={`campaign-delete-${c.campaign_id}`} title="Delete permanently">
                          <Trash size={12} />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Campaign details dialog */}
      <Dialog open={!!openCamp} onOpenChange={(o) => !o && setOpenCamp(null)}>
        <DialogContent className="max-w-2xl" data-testid="campaign-details-dialog">
          <DialogHeader>
            <DialogTitle>{openCamp?.name}</DialogTitle>
            <DialogDescription>{openCamp?.template_type} · {openCamp?.audience}</DialogDescription>
          </DialogHeader>
          {openCamp && (
            <div className="space-y-2 text-sm">
              <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                <div><span className="text-text_muted">campaign_id:</span> <code className="text-xs">{openCamp.campaign_id}</code></div>
                <div><span className="text-text_muted">creator:</span> {openCamp.created_by}</div>
                <div><span className="text-text_muted">created:</span> {formatInTimezone(openCamp.created_at, platformTz, { second: '2-digit' })}</div>
                <div><span className="text-text_muted">scheduled:</span> {formatInTimezone(openCamp.scheduled_at, platformTz, { second: '2-digit' }) || '—'}</div>
                <div><span className="text-text_muted">completed:</span> {formatInTimezone(openCamp.completed_at, platformTz, { second: '2-digit' }) || '—'}</div>
                <div><span className="text-text_muted">recipients:</span> {openCamp.recipient_count}</div>
              </div>
              <div>
                <p className="text-xs uppercase text-text_muted mt-3 mb-1">Status breakdown</p>
                <div className="grid grid-cols-6 gap-2">
                  {STATUS_TILES.map((t) => (
                    <div key={t.key} className="text-center">
                      <p className="text-xs text-text_muted">{t.label}</p>
                      <p className={`text-lg font-bold ${t.color}`}>{openCamp.status_counts?.[t.key] || 0}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setOpenCamp(null)} className="bg-primary text-white">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};


/* ──────────────────────────────────────────────────────────────
   TAB — Senders (multiple From identities)
   ────────────────────────────────────────────────────────────── */
const emptySenderForm = {
  sender_id: null,
  email: '',
  display_name: '',
  is_default: false,
  is_active: true,
  use_smtp_override: false,
  smtp_override: {
    host: '',
    port: 587,
    username: '',
    password: '',
    encryption: 'tls',
    password_set: false,
  },
};

const EmailSendersPanel = () => {
  const [senders, setSenders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(emptySenderForm);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/email-senders`, { withCredentials: true });
      setSenders(data.items || []);
    } catch { toast.error('Failed to load senders'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const startCreate = () => {
    setForm(emptySenderForm);
    setEditing(true);
  };

  const startEdit = (s) => {
    setForm({
      sender_id: s.sender_id,
      email: s.email,
      display_name: s.display_name,
      is_default: !!s.is_default,
      is_active: !!s.is_active,
      use_smtp_override: !!s.smtp_override,
      smtp_override: {
        host: s.smtp_override?.host || '',
        port: s.smtp_override?.port || 587,
        username: s.smtp_override?.username || '',
        password: '',
        encryption: s.smtp_override?.encryption || 'tls',
        password_set: !!s.smtp_override?.password_set,
      },
    });
    setEditing(true);
  };

  const save = async () => {
    if (!form.email || !form.display_name) {
      toast.error('Email and display name are required');
      return;
    }
    setSaving(true);
    try {
      const body = {
        email: form.email.trim(),
        display_name: form.display_name.trim(),
        is_default: form.is_default,
        is_active: form.is_active,
      };
      if (form.use_smtp_override) {
        body.smtp_override = {
          host: form.smtp_override.host.trim(),
          port: form.smtp_override.port,
          username: form.smtp_override.username.trim(),
          encryption: form.smtp_override.encryption,
          password: form.smtp_override.password || null,
        };
      } else if (form.sender_id) {
        body.clear_smtp_override = true;
      }
      if (form.sender_id) {
        await axios.put(`${API}/admin/email-senders/${form.sender_id}`, body, { withCredentials: true });
        toast.success('Sender updated');
      } else {
        await axios.post(`${API}/admin/email-senders`, body, { withCredentials: true });
        toast.success('Sender created');
      }
      setEditing(false);
      setForm(emptySenderForm);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const del = async (s) => {
    if (!window.confirm(`Delete sender ${s.email}?`)) return;
    try {
      await axios.delete(`${API}/admin/email-senders/${s.sender_id}`, { withCredentials: true });
      toast.success('Sender deleted');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };

  return (
    <div data-testid="email-senders-panel" className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-text_muted">
          Multiple From identities — pick one per campaign or notification type.
          Optional per-sender SMTP override lets a specific mailbox route through
          a different Hostinger account.
        </p>
        <Button onClick={startCreate} className="bg-primary text-white" data-testid="sender-new-btn">
          <Plus size={14} className="mr-2" /> New sender
        </Button>
      </div>

      {editing && (
        <div className="bg-white border border-border rounded-lg p-5 space-y-4" data-testid="sender-form">
          <h3 className="font-semibold">{form.sender_id ? 'Edit sender' : 'New sender'}</h3>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Email address *</Label>
              <Input
                type="email" value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="noreply@yourdomain.tld"
                data-testid="sender-email"
              />
            </div>
            <div>
              <Label>Display name *</Label>
              <Input
                value={form.display_name}
                onChange={(e) => setForm({ ...form, display_name: e.target.value })}
                placeholder="ATÔ — Notifications"
                data-testid="sender-display-name"
              />
            </div>
          </div>
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.is_default}
                onChange={(e) => setForm({ ...form, is_default: e.target.checked })}
                data-testid="sender-is-default"
              />
              <span>Default sender (used when no explicit sender picked)</span>
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.is_active}
                onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                data-testid="sender-is-active"
              />
              <span>Active</span>
            </label>
          </div>

          <div className="pt-3 border-t border-border">
            <label className="flex items-center gap-2 text-sm font-medium">
              <input
                type="checkbox"
                checked={form.use_smtp_override}
                onChange={(e) => setForm({ ...form, use_smtp_override: e.target.checked })}
                data-testid="sender-use-smtp-override"
              />
              <span>Use dedicated SMTP for this sender</span>
            </label>
            <p className="text-xs text-text_muted mt-1">
              Off → global SMTP. On → this sender routes through its own host/port/username/password.
            </p>
          </div>

          {form.use_smtp_override && (
            <div className="grid grid-cols-2 gap-3 bg-surface p-4 rounded" data-testid="sender-smtp-override-block">
              <div>
                <Label>SMTP host</Label>
                <Input
                  value={form.smtp_override.host}
                  onChange={(e) => setForm({ ...form, smtp_override: { ...form.smtp_override, host: e.target.value } })}
                  placeholder="smtp.hostinger.com"
                  data-testid="sender-smtp-host"
                />
              </div>
              <div>
                <Label>Port</Label>
                <Input
                  type="number"
                  value={form.smtp_override.port}
                  onChange={(e) => setForm({ ...form, smtp_override: { ...form.smtp_override, port: parseInt(e.target.value || '587', 10) } })}
                  data-testid="sender-smtp-port"
                />
              </div>
              <div>
                <Label>Username</Label>
                <Input
                  value={form.smtp_override.username}
                  onChange={(e) => setForm({ ...form, smtp_override: { ...form.smtp_override, username: e.target.value } })}
                  data-testid="sender-smtp-username"
                />
              </div>
              <div>
                <Label>Encryption</Label>
                <Select
                  value={form.smtp_override.encryption}
                  onValueChange={(v) => setForm({ ...form, smtp_override: { ...form.smtp_override, encryption: v } })}
                >
                  <SelectTrigger data-testid="sender-smtp-encryption"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="tls">STARTTLS (587)</SelectItem>
                    <SelectItem value="ssl">SSL (465)</SelectItem>
                    <SelectItem value="none">None</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2">
                <Label>Password</Label>
                <SecretInput
                  value={form.smtp_override.password}
                  onChange={(v) => setForm({ ...form, smtp_override: { ...form.smtp_override, password: v } })}
                  configured={form.smtp_override.password_set}
                  testId="sender-smtp-password"
                  placeholder="SMTP password"
                />
              </div>
            </div>
          )}

          <div className="flex items-center justify-end gap-2 pt-3 border-t border-border">
            <Button variant="outline" onClick={() => { setEditing(false); setForm(emptySenderForm); }} data-testid="sender-cancel-btn">
              Cancel
            </Button>
            <Button onClick={save} disabled={saving} className="bg-primary text-white" data-testid="sender-save-btn">
              <FloppyDisk size={14} className="mr-2" /> {saving ? 'Saving…' : 'Save sender'}
            </Button>
          </div>
        </div>
      )}

      <div className="bg-white border border-border rounded-lg overflow-x-auto" data-testid="senders-table">
        <table className="min-w-full text-sm">
          <thead className="bg-surface text-text_muted text-xs uppercase">
            <tr>
              <th className="px-4 py-2 text-left">Display name</th>
              <th className="px-4 py-2 text-left">Email</th>
              <th className="px-4 py-2 text-left">Default</th>
              <th className="px-4 py-2 text-left">Active</th>
              <th className="px-4 py-2 text-left">SMTP override</th>
              <th className="px-4 py-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={6} className="text-center py-6 text-text_muted">Loading…</td></tr>
            )}
            {!loading && senders.length === 0 && (
              <tr><td colSpan={6} className="text-center py-6 text-text_muted">
                No senders yet. Click <strong>New sender</strong> to create the first one.
              </td></tr>
            )}
            {senders.map((s) => (
              <tr key={s.sender_id} className="border-t border-border" data-testid={`sender-row-${s.sender_id}`}>
                <td className="px-4 py-2 font-medium">{s.display_name}</td>
                <td className="px-4 py-2"><code className="text-xs">{s.email}</code></td>
                <td className="px-4 py-2">
                  {s.is_default && <Badge className="bg-primary text-white">default</Badge>}
                </td>
                <td className="px-4 py-2">
                  {s.is_active
                    ? <Badge className="bg-success text-white">active</Badge>
                    : <Badge className="bg-text_muted text-white">inactive</Badge>}
                </td>
                <td className="px-4 py-2 text-xs text-text_muted">
                  {s.smtp_override?.host ? `${s.smtp_override.host}:${s.smtp_override.port}` : 'global SMTP'}
                </td>
                <td className="px-4 py-2 text-right">
                  <Button variant="outline" size="sm" onClick={() => startEdit(s)} data-testid={`sender-edit-${s.sender_id}`}>
                    <PencilSimple size={14} />
                  </Button>
                  <Button
                    variant="outline" size="sm" onClick={() => del(s)}
                    disabled={senders.length <= 1}
                    className="ml-2 text-danger" data-testid={`sender-delete-${s.sender_id}`}
                  >
                    <Trash size={14} />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
